import { kaplay } from "kaplay";

// --- Constants ---
const FLOOR_HEIGHT = 48;
const JUMP_FORCE = 750;
const INITIAL_SPEED = 300;

// --- Initialization ---
kaplay();

// --- Assets ---
loadRoot("./");

loadSound("backgroundMusic", "music/backgroundMusic.mp3");
loadSprite("backgroundImage", "sprites/background-image.jpg");
loadSprite("pennywise", "sprites/pennywise/pennywise.png");
loadSprite("pennywiseRunning", "sprites/pennywise/pennywise-running.png", {
    sliceX: 6,
    sliceY: 6,
    anims: {
        run: {
            from: 0,
            to: 35,
            speed: 30,
            loop: true,
        },
    },
});
loadSprite("pennywiseJumping", "sprites/pennywise/pennywise-jumping.png", {
    sliceX: 6,
    sliceY: 6,
    anims: {
        jump: {
            from: 0,
            to: 35,
            speed: 50,
            loop: false,
        }
    }
});
loadSprite("childRunning", "sprites/child1/child-running.png", {
    sliceX: 6,
    sliceY: 6,
    anims: {
        run: {
            from: 0,
            to: 35,
            speed: 70,
            loop: true,
        },
    },
});
loadSprite("boneco-de-neve", "sprites/obstáculos/boneco-de-neve.png",);
loadSprite("balloon", "sprites/coletáveis/balloon.png");

loadShader("selectiveRed", null, `
    vec4 frag(vec2 pos, vec2 uv, vec4 color, sampler2D tex) {
        vec4 c = def_frag();
        float gray = dot(c.rgb, vec3(0.299, 0.587, 0.114));
        gray = (gray - 0.5) * 1.5 + 0.6;
        float redness = c.r - max(c.g, c.b);
        float mask = smoothstep(0.1, 0.25, redness);
        vec3 lowSat = mix(vec3(gray), c.rgb, 0.3);
        return vec4(mix(lowSat * 0.4, c.rgb * 0.4, mask), c.a);
    }
`);

// --- Game Logic ---
scene("game", () => {
    // 1. Setup & Variables
    usePostEffect("selectiveRed");
    let speed = INITIAL_SPEED;
    let score = 0;
    setGravity(1600);

    // 2. Environment (Background, Floor, Snow)
    const bg1 = add([
        sprite("backgroundImage"),
        pos(0, 0),
        scale(width() / 1024, height() / 572),
        fixed(),
        z(-1),
        move(LEFT, speed / 10),
        "background",
    ]);

    const bg2 = add([
        sprite("backgroundImage"),
        pos(width(), 0),
        scale(width() / 1024, height() / 572),
        fixed(),
        z(-1),
        move(LEFT, speed / 10),
        "background",
    ]);

    onUpdate("background", (bg) => {
        if (bg.pos.x <= -width()) {
            bg.pos.x += width() * 2;
        }
    });

    const floor = add([
        rect(width(), FLOOR_HEIGHT),
        pos(0, height()),
        anchor("botleft"),
        area(),
        body({ isStatic: true }),
        color(255, 255, 255),
        outline(2),
        "floor",
    ]);

    loop(0.05, () => {
        add([
            circle(rand(1, 3)),
            pos(rand(0, width()), -10),
            color(255, 255, 255),
            move(DOWN, rand(20, 100)),
            opacity(rand(0.5, 0.8)),
            offscreen({ destroy: true }),
            z(100),
            "snow",
        ]);
    });

    // 3. Characters (Player, Child)
    const player = add([
        sprite("pennywiseRunning"),
        pos(width() / 5, height() - 90),
        anchor("center"),
        area({ scale: vec2(0.5, 1) }),
        body(),
        scale(0.6),
    ]);

    player.play("run");

    const child = add([
        sprite("childRunning"),
        pos(width() - 140, height() - 90),
        anchor("center"),
        area({ scale: vec2(0.5, 1) }),
        body(),
        scale(0.35),
    ]);

    child.play("run");

    onResize(() => {
        bg1.scale = vec2(width() / 1024, height() / 572);
        bg2.scale = vec2(width() / 1024, height() / 572);
        floor.width = width();
        floor.pos.y = height();
        player.pos.x = width() / 5;
        player.pos.y = height() - 90;
        child.pos.x = width() - 140;
        child.pos.y = height() - 90;
    });

    // 4. Input & Control
    function jump() {
        if (player.isGrounded()) {
            player.use(sprite("pennywiseJumping"));
            player.play("jump");
            player.jump(JUMP_FORCE);
        };
    };

    player.onGround(() => {
        player.use(sprite("pennywiseRunning"));
        player.play("run");
    });

    onKeyPress("space", jump);
    onClick(jump);

    // 5. Spawners
    function spawnBalloon() {
        add([
            sprite("balloon"),
            pos(width(), height() - FLOOR_HEIGHT - 270),
            area({ scale: vec2(0.4, 0.5) }),
            anchor("top"),
            move(LEFT, speed),
            scale(0.15),
            offscreen({ destroy: true }),
            "balloon",
        ]);

        wait(rand(10, 20), spawnBalloon);
    }
    spawnBalloon();

    function spawnObstacle() {
        add([
            sprite("boneco-de-neve"),
            area({ scale: vec2(0.3, 0.5) }),
            pos(width(), height() - FLOOR_HEIGHT - 40),
            anchor("center"),
            move(LEFT, speed),
            scale(0.2),
            offscreen({ destroy: true }),
            "boneco-de-neve",
        ]);

        wait(rand(2, 2.5), spawnObstacle);
    }
    spawnObstacle();

    // 6. Collisions
    player.onCollide("balloon", (balloon) => {
        destroy(balloon);

        for (let i = 0; i < 15; i++) {
            add([
                rect(rand(3, 5), rand(3, 5)),
                pos(balloon.pos),
                color(rand(0, 255), rand(0, 255), rand(0, 255)),
                anchor("center"),
                move(vec2(rand(-1, 1), rand(-1, 1)).unit(), rand(100, 300)),
                rotate(rand(0, 360)),
                body(),
                offscreen({ destroy: true }),
                "confete"
            ]);
        };

        score += 50;
        add([
            text("+50"),
            pos(balloon.pos),
            color(255, 0, 0),
            anchor("center"),
            move(UP, 100),
            offscreen({ destroy: true }),
            "balloon-score"
        ]);
    });

    player.onCollide("boneco-de-neve", () => {
        go("lose", score);
    });

    // 7. UI & Game Loop
    const scoreLabel = add([text(score), pos(24, 24)]);

    onUpdate(() => {
        score += 0.05;
        scoreLabel.text = score.toFixed();
        speed += 0.01;
    });
});

scene("lose", (score) => {
    const bg = add([
        sprite("backgroundImage"),
        pos(0, 0),
        scale(width() / 1024, height() / 572),
        fixed(),
        z(-1),
        "background",
    ]);

    const floor = add([
        rect(width(), FLOOR_HEIGHT),
        pos(0, height()),
        anchor("botleft"),
        area(),
        body({ isStatic: true }),
        color(255, 255, 255),
        outline(2),
        "floor",
    ]);
    
    const pennywise = add([
        sprite("pennywise"),
        pos(width() / 2, height() / 2 - 80),
        scale(0.6),
        anchor("center"),
    ]);

    const scoreText = add([
        text(score.toFixed()),
        pos(width() / 2, height() / 2 + 80),
        scale(2),
        anchor("center"),
    ]);

    onResize(() => {
        bg.scale = vec2(width() / 1024, height() / 572);
        floor.width = width();
        floor.pos.y = height();
        pennywise.pos = vec2(width() / 2, height() / 2 - 80);
        scoreText.pos = vec2(width() / 2, height() / 2 + 80);
    });

    onKeyPress("space", () => go("game"));
    onClick(() => go("game"));
});

scene("start", () => {
    usePostEffect("selectiveRed");

    const bg = add([
        sprite("backgroundImage"),
        pos(0, 0),
        scale(width() / 1024, height() / 572),
        fixed(),
        z(-1),
    ]);

    const floor = add([
        rect(width(), FLOOR_HEIGHT),
        pos(0, height()),
        anchor("botleft"),
        color(255, 255, 255),
        outline(2),
    ]);

    const player = add([
        sprite("pennywise"),
        pos(width() / 5, height() - 90),
        anchor("center"),
        scale(0.6),
    ]);

    const child = add([
        sprite("childRunning"),
        pos(width() - 140, height() - 90),
        anchor("center"),
        scale(0.35),
    ]);

    const startText = add([
        text("Click to Play"),
        pos(width() / 2, height() / 2),
        anchor("center"),
        scale(1.5),
    ]);

    onResize(() => {
        bg.scale = vec2(width() / 1024, height() / 572);
        floor.width = width();
        floor.pos.y = height();
        player.pos.x = width() / 5;
        player.pos.y = height() - 90;
        child.pos.x = width() - 140;
        child.pos.y = height() - 90;
        startText.pos = vec2(width() / 2, height() / 2);
    });

    const startGame = () => {
        play("backgroundMusic", {
            volume: 0.3,
            speed: 1,
            loop: true,
        });
        go("game");
    };

    onKeyPress("space", startGame);
    onClick(startGame);
});

// --- Start ---
go("start");